import { useState } from 'react'
import { TrashIcon } from '../components/icons/TrashIcon'
import { GridIcon } from '../components/icons/GridIcon'
import { ListIcon } from '../components/icons/ListIcon'
import { PencilIcon } from '../components/icons/PencilIcon'
import { PlusIcon } from '../components/icons/PlusIcon'
import { useNavigate } from 'react-router-dom'
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch, ApiError } from '../lib/api'
import { toAgentSlug } from '../lib/agentSlug'
import type { AgentListItem, AgentListResponse, AgentStatus, AgentStatusResponse, FeaturesResponse } from '../lib/types'
import { Spinner } from '../components/Spinner'
import { Toast } from '../components/Toast'
import { NewAgentModal } from '../components/NewAgentModal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useAuth } from '../auth/useAuth'
import { AgentsPreview } from './PreviewPage'
import { PageHeader } from '../components/PageHeader'

/* ─── Mambu-built badge ─── */

function MambuBadge() {
  const { t } = useTranslation()
  return (
    <span className="badge badge-mambu" style={{ marginLeft: 6 }}>
      {t('common.mambuBuilt')}
    </span>
  )
}

/* ─── Agent avatar ───
   Every Agent shows the same fixed picture — the Mambu mark on a filled square.
   There is no per-Agent emoji/image picker. */

function AgentAvatar({ size = 44 }: { size?: number }) {
  return (
    <div
      aria-hidden="true"
      className="agent-grid-icon mambu-mark-icon"
      style={{ width: size, height: size, borderRadius: size === 44 ? 10 : 8 }}
    >
      <img src="/mambu-logo.png" alt="" />
    </div>
  )
}

/* ─── Action buttons ─── */

function IconBtn({
  title,
  disabled = false,
  danger = false,
  onClick,
  children,
}: {
  title: string
  disabled?: boolean
  danger?: boolean
  onClick?: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`icon-btn${danger ? ' danger' : ''}`}
    >
      {children}
    </button>
  )
}

/* ─── Grid card ─── */

function AgentGridCard({
  item,
  onNavigate,
  onKeyDown,
  onDelete,
  onStatusToggle,
  allowCustomAgents,
}: {
  item: AgentListItem
  onNavigate: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onDelete: () => void
  onStatusToggle: (targetStatus: AgentStatus) => void
  allowCustomAgents: boolean
}) {
  const { t } = useTranslation()

  return (
    <div
      role="button"
      tabIndex={0}
      data-agent-card
      aria-label={`Open ${item.displayName}`}
      onClick={onNavigate}
      onKeyDown={onKeyDown}
      className="agent-grid-card"
    >
      <div className="agent-grid-card-header">
        <AgentAvatar size={44} />
        <div className="status-toggle-pill" onClick={(e) => e.stopPropagation()}>
          <label className="toggle">
            <input
              type="checkbox"
              checked={item.status === 'Enabled'}
              onChange={() => onStatusToggle(item.status === 'Enabled' ? 'Disabled' : 'Enabled')}
              aria-label={t('agents.statusAriaLabel', { status: item.status })}
            />
            <span className="toggle-track"></span>
          </label>
        </div>
      </div>

      <div className="agent-grid-name">
        {item.displayName}
        {item.isMambuBuilt && <MambuBadge />}
      </div>
      <div className="agent-grid-category">{item.category}</div>
      <div className="agent-grid-desc">{item.description ?? ''}</div>

      <div className="agent-grid-footer">
        <span className="agent-grid-meta">
          {new Date(item.lastModifiedDate).toLocaleDateString()}
        </span>
        <div
          className="agent-list-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <IconBtn title={t('agents.actions.edit')} onClick={onNavigate}>
            <span style={{ color: 'var(--accent)' }}><PencilIcon /></span>
          </IconBtn>
          <IconBtn
            title={
              item.isMambuBuilt
                ? t('agents.actions.cannotDeleteMambu')
                : !allowCustomAgents
                  ? t('agents.actions.customDeleteDisabled')
                  : t('agents.actions.delete')
            }
            disabled={item.isMambuBuilt || !allowCustomAgents}
            danger
            onClick={onDelete}
          >
            <TrashIcon />
          </IconBtn>
        </div>
      </div>
    </div>
  )
}

/* ─── List row ─── */

function AgentListRow({
  item,
  onNavigate,
  onKeyDown,
  onDelete,
  onStatusToggle,
  allowCustomAgents,
}: {
  item: AgentListItem
  onNavigate: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onDelete: () => void
  onStatusToggle: (targetStatus: AgentStatus) => void
  allowCustomAgents: boolean
}) {
  const { t } = useTranslation()

  return (
    <div
      role="button"
      tabIndex={0}
      data-agent-card
      aria-label={`Open ${item.displayName}`}
      onClick={onNavigate}
      onKeyDown={onKeyDown}
      className="agent-list-row"
    >
      <AgentAvatar size={36} />

      <div className="agent-list-info">
        <div className="agent-grid-name">
          {item.displayName}
          {item.isMambuBuilt && <MambuBadge />}
        </div>
        <div className="agent-grid-desc">{item.description ?? ''}</div>
      </div>

      <span className="agent-list-meta">{item.category}</span>

      <span className="agent-list-meta">
        {new Date(item.lastModifiedDate).toLocaleDateString()}
      </span>

      <div className="status-toggle-pill" onClick={(e) => e.stopPropagation()}>
        <label className="toggle">
          <input
            type="checkbox"
            checked={item.status === 'Enabled'}
            onChange={() => onStatusToggle(item.status === 'Enabled' ? 'Disabled' : 'Enabled')}
            aria-label={t('agents.statusAriaLabel', { status: item.status })}
          />
          <span className="toggle-track"></span>
        </label>
      </div>

      <div
        className="agent-list-actions"
        onClick={(e) => e.stopPropagation()}
      >
        <IconBtn title={t('agents.actions.edit')} onClick={onNavigate}>
          <span style={{ color: 'var(--accent)' }}><PencilIcon /></span>
        </IconBtn>
        <IconBtn
          title={
            item.isMambuBuilt
              ? t('agents.actions.cannotDeleteMambu')
              : !allowCustomAgents
                ? t('agents.actions.customDeleteDisabled')
                : t('agents.actions.delete')
          }
          disabled={item.isMambuBuilt || !allowCustomAgents}
          onClick={onDelete}
        >
          <TrashIcon />
        </IconBtn>
      </div>
    </div>
  )
}

/* ─── New Agent dashed card ─── */

function NewAgentCard({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('agents.empty.title')}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      className="agent-grid-card new-agent"
    >
      <div style={{ fontSize: 28, marginBottom: 8, fontWeight: 300 }}>+</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{t('agents.empty.title')}</div>
      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('agents.empty.subtitle')}</div>
    </div>
  )
}

/* ─── View toggle button ─── */

function ViewToggleBtn({
  active,
  title,
  onClick,
  children,
  borderLeft = false,
}: {
  active: boolean
  title: string
  onClick: () => void
  children: React.ReactNode
  borderLeft?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      className={`agents-view-btn${active ? ' active' : ''}`}
      style={{
        padding: '6px 10px',
        border: 'none',
        borderLeft: borderLeft ? '1px solid var(--border)' : 'none',
        borderRadius: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {children}
    </button>
  )
}

/* ─── Arrow-key card navigation ─── */

function handleCardKeyNav(e: React.KeyboardEvent<HTMLDivElement>, onEnter: () => void) {
  if (e.key === 'Enter') {
    e.preventDefault()
    onEnter()
    return
  }
  if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
    e.preventDefault()
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-agent-card]'))
    const idx = cards.indexOf(e.currentTarget)
    cards[idx + 1]?.focus()
  } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
    e.preventDefault()
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-agent-card]'))
    const idx = cards.indexOf(e.currentTarget)
    cards[idx - 1]?.focus()
  }
}

/* ─── Main page ─── */

export function Agents() {
  const { agentsContracted } = useAuth()
  if (!agentsContracted) return <AgentsPreview />
  return <AgentsOverview />
}

function AgentsOverview() {
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [view, setView] = useState<'grid' | 'list'>(() => {
    const stored = localStorage.getItem('agents_view') ?? localStorage.getItem('sparks_view')
    return stored === 'grid' ? 'grid' : 'list'
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<AgentListItem | null>(null)
  const [pendingStatusChange, setPendingStatusChange] =
    useState<{ item: AgentListItem; targetStatus: AgentStatus } | null>(null)

  const { data: features } = useQuery({
    queryKey: ['features'],
    queryFn: () => apiFetch<FeaturesResponse>('/api/v1/features'),
  })

  const { data: mambuData, isLoading: mambuLoading } = useQuery({
    queryKey: ['agents', 'mambu'],
    queryFn: () => apiFetch<AgentListResponse>('/api/v1/agents?mambuBuilt=true&limit=100'),
  })

  const { data: customData, isLoading: customLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['agents', 'custom'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      apiFetch<AgentListResponse>(`/api/v1/agents?mambuBuilt=false${pageParam ? `&cursor=${pageParam}` : ''}`),
    getNextPageParam: (lastPage: AgentListResponse) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  })

  const deleteMutation = useMutation({
    mutationFn: (agentId: string) =>
      apiFetch<void>(`/api/v1/agents/${agentId}`, { method: 'DELETE' }),
    onSuccess: (_data, agentId) => {
      const name = pendingDelete?.displayName ?? 'Agent'
      void queryClient.invalidateQueries({ queryKey: ['agents'] })
      setPendingDelete(null)
      setToast(t('agents.deleted', { name }))
      void agentId
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('agents.failedToLoad')
      setPendingDelete(null)
      setToast(message)
    },
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AgentStatus }) =>
      apiFetch<AgentStatusResponse>(`/api/v1/agents/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agents'] })
      setPendingStatusChange(null)
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('agents.failedToLoad')
      setPendingStatusChange(null)
      setToast(message)
    },
  })

  const mambuBuilt = mambuData?.items ?? []
  const custom = customData?.pages.flatMap((p) => p.items) ?? []
  const isLoading = mambuLoading || customLoading
  const allowCustomAgents = features?.allowCustomAgents ?? features?.allowCustomSparks ?? false

  function handleViewChange(v: 'grid' | 'list') {
    setView(v)
    localStorage.setItem('agents_view', v)
  }

  function agentDetailPath(item: AgentListItem) {
    return `/agents/${toAgentSlug(item.displayName)}/agent-dna`
  }

  function renderCards(items: AgentListItem[], allowCustom = true) {
    if (view === 'grid') {
      return items.map((item) => {
        const goToItem = () => navigate(agentDetailPath(item))
        return (
          <AgentGridCard
            key={item.id}
            item={item}
            onNavigate={goToItem}
            onKeyDown={(e) => handleCardKeyNav(e, goToItem)}
            onDelete={() => setPendingDelete(item)}
            onStatusToggle={(targetStatus) => setPendingStatusChange({ item, targetStatus })}
            allowCustomAgents={allowCustom}
          />
        )
      })
    }
    return items.map((item) => {
      const goToItem = () => navigate(agentDetailPath(item))
      return (
        <AgentListRow
          key={item.id}
          item={item}
          onNavigate={goToItem}
          onKeyDown={(e) => handleCardKeyNav(e, goToItem)}
          onDelete={() => setPendingDelete(item)}
          onStatusToggle={(targetStatus) => setPendingStatusChange({ item, targetStatus })}
          allowCustomAgents={allowCustom}
        />
      )
    })
  }

  return (
    <div className="page-view">
      <div className="page-header">
      <PageHeader
        title={t('agents.heading')}
        caption={t('agents.subtitle')}
        actions={
          <>
            {/* View toggle */}
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <ViewToggleBtn active={view === 'grid'} title={t('agents.gridView')} onClick={() => handleViewChange('grid')}>
                <GridIcon />
              </ViewToggleBtn>
              <ViewToggleBtn active={view === 'list'} title={t('agents.listView')} onClick={() => handleViewChange('list')} borderLeft>
                <ListIcon />
              </ViewToggleBtn>
            </div>
            {/* New Agent button */}
            {allowCustomAgents && (
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="btn btn-primary"
              >
                <PlusIcon />
                {t('agents.newAgent')}
              </button>
            )}
          </>
        }
      />
      </div>{/* end .page-header */}

      <div className="page-body">
      {/* Loading state */}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Spinner size={32} />
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '16px 20px', color: 'var(--danger)', fontSize: 13 }}>
          {error instanceof Error ? error.message : t('agents.failedToLoad')}
        </div>
      )}

      {/* Content */}
      {(mambuData || customData) && (
        <>
          {/* Mambu-built section */}
          {mambuBuilt.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div className="section-label">{t('agents.sections.mambuBuilt')}</div>
              <div className={`agents-grid${view === 'grid' ? '' : ' hidden'}`}>
                {renderCards(mambuBuilt)}
              </div>
              <div className={`agents-list${view === 'list' ? ' active' : ''}`}>
                {renderCards(mambuBuilt)}
              </div>
            </div>
          )}

          {/* Custom Agents section */}
          {custom.length > 0 && (
            <div>
              <div className="section-label">{t('agents.sections.custom')}</div>
              <div className={`agents-grid${view === 'grid' ? '' : ' hidden'}`}>
                {renderCards(custom, true)}
                {allowCustomAgents && <NewAgentCard onClick={() => setIsModalOpen(true)} />}
              </div>
              <div className={`agents-list${view === 'list' ? ' active' : ''}`}>
                {renderCards(custom, true)}
                {allowCustomAgents && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={t('agents.empty.title')}
                    onClick={() => setIsModalOpen(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsModalOpen(true) } }}
                    className="agent-list-row"
                    style={{ justifyContent: 'center', color: 'var(--text-tertiary)', border: '1px dashed var(--border)' }}
                  >
                    <span style={{ fontSize: 16 }}>+</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{t('agents.empty.title')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Load more */}
          {hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? t('agents.loading') : t('agents.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
      </div>{/* end .page-body */}

      {/* Modal */}
      {isModalOpen && (
        <NewAgentModal
          onClose={() => setIsModalOpen(false)}
          onError={(msg) => setToast(msg)}
        />
      )}

      {/* Delete confirmation */}
      {pendingDelete && (
        <ConfirmDialog
          title={t('agents.deleteDialog.title', { name: pendingDelete.displayName })}
          message={t('agents.deleteDialog.message')}
          confirmLabel={deleteMutation.isPending ? t('agents.deleteDialog.confirming') : t('agents.deleteDialog.confirm')}
          danger
          onCancel={() => { if (!deleteMutation.isPending) setPendingDelete(null) }}
          onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
        />
      )}

      {/* Status change confirmation */}
      {pendingStatusChange && (
        <ConfirmDialog
          title={pendingStatusChange.targetStatus === 'Enabled' ? t('agentDetail.statusDialog.enableTitle') : t('agentDetail.statusDialog.disableTitle')}
          message={
            pendingStatusChange.targetStatus === 'Enabled'
              ? t('agentDetail.statusDialog.enableMessage')
              : t('agentDetail.statusDialog.disableMessage')
          }
          confirmLabel={pendingStatusChange.targetStatus === 'Enabled' ? t('agentDetail.statusDialog.enable') : t('agentDetail.statusDialog.disable')}
          danger={pendingStatusChange.targetStatus === 'Disabled'}
          requiresAcknowledgment
          acknowledgmentLabel={t('agentDetail.statusDialog.acknowledgment')}
          onConfirm={() =>
            statusMutation.mutate({ id: pendingStatusChange.item.id, status: pendingStatusChange.targetStatus })
          }
          onCancel={() => setPendingStatusChange(null)}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  )
}
